import React, { useContext, useMemo, useCallback, useState, useEffect } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { BoxClass } from '../../../helper/classes'
import { GlobalContext, globalOnDemandVars } from '../../../helper/context'
import { firstLetterCapitalize, truncateDefault } from '../../../helper/functions'
import { Button, Card, Collapse, HTMLTable, Tag } from '@blueprintjs/core'
import { getTodayNumber } from '../../../logic/functions'
import { useLocation } from 'react-router-dom'
import { getMemorizingStatus } from '../../../logic/notebook'
import { getNewRecord, getRatingAsNumber } from '../../../logic/SM2+'
import produce from 'immer'

const regExpForClozeReplace = /{{.*?}}/g

function NodeInformationAddOn () {
  const context = useContext(GlobalContext)
  const location = useLocation()

  const noInfo = useMemo(() => (
    <div css={css`padding: 0 20px;`}>
      <br />
      This does not show any information about the notes in the Playground.<br />It shows the difficulty and the next due date or something of each for nodes of the Organize section.
    </div>
  ), [])

  const getNodeInfoInDetail = useCallback(
    (nodeInfo) => {
      let LastLearnedDate
      let dueDateMinusToday
      if (nodeInfo) {
        LastLearnedDate = ((nodeInfo.ratingRecord && nodeInfo.ratingRecord.length) ? (
          new Date(nodeInfo.ratingRecord[nodeInfo.ratingRecord.length - 1].time)) : null)

        dueDateMinusToday = (nodeInfo.update + nodeInfo.interval) - getTodayNumber(true)
      }

      const failStatus = nodeInfo.failStatus
      let StatusDescription
      if (failStatus) {
        if (failStatus === 'solved') {
          StatusDescription = LastLearnedDate ? (dueDateMinusToday >= 1 ? 'Memorized' : 'To Review') : 'New'
        } else if (failStatus === 'reStudy') {
          StatusDescription = 'To Learn Again'
        } else if (failStatus === 'reTest') {
          StatusDescription = 'To Test Again'
        }
      } else {
        StatusDescription = LastLearnedDate ? (dueDateMinusToday >= 1 ? 'Memorized' : 'To Review') : 'New'
      }

      return (
        <div>
          {(!nodeInfo.subtitle && !nodeInfo.isSpreadsheetNode && !nodeInfo.isUploadType2Node) ? (
            <HTMLTable>
              <tbody>
                <tr>
                  <td><b>Status</b></td>
                  <td>Context Node</td>
                </tr>
                <tr>
                  <td><b>Children</b></td>
                  <td>{`${(nodeInfo.node && nodeInfo.node.children) ? nodeInfo.node.children.length : 0} Nodes`}</td>
                </tr>
              </tbody>
            </HTMLTable>
          ) : (
            <HTMLTable bordered condensed>
              <tbody>
                <tr>
                  <td><b>Status</b></td>
                  <td>{`${StatusDescription}`}</td>
                </tr>
                {(nodeInfo.node && (!failStatus || failStatus === 'solved') && LastLearnedDate) ? (
                  <tr>
                    <td><b>Predicted Rating</b></td>
                    <td>{firstLetterCapitalize(getMemorizingStatus(nodeInfo.node))}</td>
                  </tr>
                ) : null}
                <tr>
                  <td><b>Reviewed</b></td>
                  <td>{`${nodeInfo.ratingRecord.length} times`}</td>
                </tr>
                <tr>
                  <td><b>Difficulty</b></td>
                  <td>{`${LastLearnedDate ? nodeInfo.difficulty : 'Not yet'}`}</td>
                </tr>
                <tr>
                  <td><b>Next Review Date</b></td>
                  <td>{`After ${dueDateMinusToday} days`}</td>
                </tr>
                <tr>
                  <td><b>Last Reviewed</b></td>
                  <td>{`${LastLearnedDate ? ((LastLearnedDate.getMonth() + 1) + ' / ' + (LastLearnedDate.getDate()) + ' / ' + LastLearnedDate.getFullYear()) : 'Not yet'}`}</td>
                </tr>
                <tr>
                  <td><b>Last Rated</b></td>
                  <td>{`${LastLearnedDate ? (
                    nodeInfo.ratingRecord[nodeInfo.ratingRecord.length - 1].rating) : ('Not yet')
                  }`}</td>
                </tr>
              </tbody>
            </HTMLTable>)}
        </div>)
    },
    []
  )

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.tron.display({
        name: 'nodeInfo',
        value: {
          node: context.focusedNode
        }
        // , important: true
      })
    }
  }, [context.focusedNode])

  const nodeInfoForOrganize = useMemo(() => ((location.pathname === '/organizing' || location.pathname.startsWith('/sharing')) &&
    (!(context.focusedNode && context.focusedNode.editableValues) ? <div css={css`text-align: center`}><br /><br />Nothing Selected</div>
      : <div>
        <br />
        <Tag title={context.focusedNode.title} large>{truncateDefault(context.focusedNode.title)}</Tag>
        {getNodeInfoInDetail({
          difficulty: context.focusedNode.editableValues.difficulty,
          update: context.focusedNode.editableValues.update,
          interval: context.focusedNode.editableValues.interval,
          ratingRecord: context.focusedNode.editableValues.ratingRecord,
          failStatus: context.focusedNode.editableValues.failStatus,
          subtitle: context.focusedNode.subtitle,
          isSpreadsheetNode: context.focusedNode.isSpreadsheetNode,
          isUploadType2Node: context.focusedNode.isUploadType2Node,
          node: context.focusedNode
        })}
      </div>)), [context.focusedNode, getNodeInfoInDetail, location.pathname])

  const [collapseArray, setCollapseArray] = useState([])
  useEffect(() => {
    if ((location.pathname === '/memorizing' || location.pathname === '/memorizingEnter') && Array.isArray(context.focusedNode)) {
      setCollapseArray(context.focusedNode.map((node) => false))
    }
  }, [context.focusedNode, location.pathname])

  useEffect(() => {
    if ((location.pathname === '/organizing' || location.pathname.startsWith('/sharing')) && Array.isArray(context.focusedNode)) {
      globalOnDemandVars.set_focusedNode(null)
    }
  }, [context.focusedNode, location.pathname])

  const nodeInfoForMemorize = useMemo(() => ((location.pathname === '/memorizing' || location.pathname === '/memorizingEnter') && (Array.isArray(context.focusedNode)
    ? (<div>{
      context.focusedNode.map((oneNodeInfo, index) => {
        if (process.env.NODE_ENV !== 'production') {
          console.tron.display({
            name: 'nodeInfoForMemorize',
            preview: oneNodeInfo.title,
            value: oneNodeInfo // , important: true
          })
        }
        const memorizingStatus = oneNodeInfo.memorizingStatus

        let possibleRatings
        let nameOfEasy = 'Easy'
        let nameOfNormal = 'Normal'
        let nameOfHard = 'Hard'
        switch (memorizingStatus) {
          case 'firstTime':
            possibleRatings = ['easy']
            nameOfEasy = 'Already Know'
            break
          case 'reTest':
            possibleRatings = ['hard']
            nameOfHard = 'Managed to Recall'
            break
          case 'reStudy':
            possibleRatings = []
            break
          default:
            possibleRatings = ['easy', 'normal', 'hard']
            break
        }

        const detailOpened = collapseArray[index]

        const string = oneNodeInfo.title.replace(regExpForClozeReplace, '{{...}}')
        return (
          <Card key={oneNodeInfo.id} css={css`padding: 4px; margin: 6px 2px;`}>
            <Tag title={string} large>{truncateDefault(string)}</Tag>

            {memorizingStatus === 'reStudy' ? null : (
              <HTMLTable bordered condensed>
                <thead>
                  <tr>
                    <th>If Rated By</th>
                    <th>Will Be Buried</th>
                  </tr>
                </thead>
                <tbody>
                  {possibleRatings.map((rating) => {
                    let ratingName
                    switch (rating) {
                      case 'easy':
                        ratingName = nameOfEasy
                        break
                      case 'normal':
                        ratingName = nameOfNormal
                        break
                      case 'hard':
                        ratingName = nameOfHard
                        break
                      default:
                        throw (new Error('possibleRating'))
                    }

                    let currentRecord
                    if ((
                      globalOnDemandVars.PhaseInstance().id === oneNodeInfo.updateIdentifier && memorizingStatus !== 'reTest'
                    ) || (
                      memorizingStatus === 'reTest' && globalOnDemandVars.PhaseInstance().id + 'reTest' === oneNodeInfo.updateIdentifier
                    )) {
                      currentRecord = oneNodeInfo.prevRecord
                    } else {
                      currentRecord = {
                        difficulty: oneNodeInfo.difficulty,
                        interval: oneNodeInfo.interval,
                        update: oneNodeInfo.update
                      }
                    }

                    const newRecordByRating = getNewRecord(currentRecord, getRatingAsNumber(rating))
                    const buriedDays = newRecordByRating.dueDate - getTodayNumber(true)

                    return (
                      <tr key={rating}>
                        <td>{ratingName}</td>
                        <td>{`For ${buriedDays} days`}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </HTMLTable>
            )}

            <Button icon={detailOpened ? 'minus' : 'caret-down'} minimal
              onClick={() => {
                setCollapseArray(produce(collapseArray, draft => {
                  draft[index] = !draft[index]
                }))
              }}>
              <span>More Details<span css={css`opacity: 0.3`}>{`(Click to ${detailOpened ? 'Hide' : 'Show'})`}</span></span>
            </Button>
            <Collapse isOpen={detailOpened}>
              {getNodeInfoInDetail(oneNodeInfo)}
            </Collapse>

          </Card>)
      })
    }</div>) : <div css={css`text-align: center`}><br /><br />Nothing Here</div>
  )), [collapseArray, context.focusedNode, getNodeInfoInDetail, location.pathname])

  return (
    (location.pathname === '/organizing' || location.pathname.startsWith('/sharing')) ? nodeInfoForOrganize : (
      (location.pathname === '/memorizingEnter' || location.pathname === '/memorizing') ? nodeInfoForMemorize : noInfo
    )
  )
}

const NodeInformationAddOnBoxClass = new BoxClass(NodeInformationAddOn, 'Node Information', null, 1, { order: 7, inSharingMode: true, onlyForTheseLocations: ['/organizing', '/sharing'] })

export const NoteInformationAddOnBoxClass = new BoxClass(NodeInformationAddOn, 'Note Information', null, 1, { order: 7, onlyForTheseLocations: ['/memorizing', '/memorizingEnter'] })

export default NodeInformationAddOnBoxClass
